import { useEffect, useState } from "react";
import { api, API } from "@/lib/apiClient";
import { usePark } from "@/contexts/ParkContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/shared";
import { Save } from "lucide-react";
import { toast } from "sonner";

export default function SettingsPage() {
  const { currentPark, refresh, loading: parksLoading } = usePark();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentPark) return;
    setCfg(null);  // reset when switching parks
    api.get(`/parks/${currentPark.id}`).then(({ data }) => setCfg(data)).catch(() => toast.error("Failed to load park"));
  }, [currentPark]);

  if (parksLoading || (!currentPark && cfg === null)) {
    return <div className="p-8 text-sm text-[#737A76]" data-testid="settings-loading">Loading…</div>;
  }
  if (!currentPark) return <div className="p-8 text-sm text-[#737A76]">No park assigned. Contact your admin.</div>;
  if (!cfg) return <div className="p-8 text-sm text-[#737A76]">Loading park…</div>;

  const update = (patch) => setCfg({ ...cfg, ...patch });
  const num = (v) => parseFloat(v) || 0;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/parks/${currentPark.id}`, cfg);
      toast.success("Saved");
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader
        title={`Park Setup — ${currentPark.name}`}
        subtitle="Park details, rent rates, taxes, and FreedomCRM connection"
        actions={<Button className="bg-[#284B3B] hover:bg-[#1E3F32] text-white" onClick={save} disabled={saving} data-testid="save-park-button"><Save size={14} className="mr-2" />{saving ? "Saving…" : "Save"}</Button>}
      />

      <Card className="border-[#EAEAE5] shadow-none mb-4"><CardContent className="p-6 space-y-4">
        <div className="text-[10px] uppercase tracking-widest text-[#737A76]">Park Details</div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Park Name" value={cfg.name} onChange={(v) => update({ name: v })} testId="park-name-input" />
          <Field label="Phone" value={cfg.phone} onChange={(v) => update({ phone: v })} />
          <Field label="Email" value={cfg.email} onChange={(v) => update({ email: v })} />
          <Field label="Country" value={cfg.country} onChange={(v) => update({ country: v })} />
        </div>
        <Field label="Address" value={cfg.address} onChange={(v) => update({ address: v })} />
        <div className="grid grid-cols-3 gap-4">
          <Field label="City" value={cfg.city} onChange={(v) => update({ city: v })} />
          <Field label="State" value={cfg.state} onChange={(v) => update({ state: v })} />
          <Field label="Zip" value={cfg.zip_code} onChange={(v) => update({ zip_code: v })} />
        </div>
      </CardContent></Card>

      <Card className="border-[#EAEAE5] shadow-none mb-4"><CardContent className="p-6 space-y-4">
        <div className="text-[10px] uppercase tracking-widest text-[#737A76]">Rent Rates</div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Short term — Daily" type="number" value={cfg.short_term_daily_rate} onChange={(v) => update({ short_term_daily_rate: num(v) })} />
          <Field label="Short term — Weekly" type="number" value={cfg.short_term_weekly_rate} onChange={(v) => update({ short_term_weekly_rate: num(v) })} />
          <Field label="Short term — Monthly" type="number" value={cfg.short_term_monthly_rate} onChange={(v) => update({ short_term_monthly_rate: num(v) })} />
          <Field label="Long term — Monthly" type="number" value={cfg.long_term_monthly_rate} onChange={(v) => update({ long_term_monthly_rate: num(v) })} />
          <Field label="Annual rate" type="number" value={cfg.annual_rate} onChange={(v) => update({ annual_rate: num(v) })} />
        </div>
      </CardContent></Card>

      <Card className="border-[#EAEAE5] shadow-none mb-4"><CardContent className="p-6 space-y-4">
        <div className="text-[10px] uppercase tracking-widest text-[#737A76]">Tax Rates (decimal, e.g. 0.06 = 6%)</div>
        <div className="grid grid-cols-3 gap-4">
          <Field label="City Tax" type="number" step="0.001" value={cfg.city_tax_rate} onChange={(v) => update({ city_tax_rate: num(v) })} />
          <Field label="State Tax" type="number" step="0.001" value={cfg.state_tax_rate} onChange={(v) => update({ state_tax_rate: num(v) })} />
          <Field label="Bed Tax" type="number" step="0.001" value={cfg.bed_tax_rate} onChange={(v) => update({ bed_tax_rate: num(v) })} />
        </div>
      </CardContent></Card>

      <Card className="border-[#EAEAE5] shadow-none mb-4"><CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#737A76]">FreedomCRM Integration</div>
            <p className="text-xs text-[#737A76] mt-1">Each park has its own FreedomCRM subaccount (Location ID + PIT token).</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#737A76]">Enabled</span>
            <Switch checked={!!cfg.ghl_enabled} onCheckedChange={(v) => update({ ghl_enabled: v })} data-testid="ghl-enabled-switch" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Location ID (subaccount)" value={cfg.ghl_location_id} onChange={(v) => update({ ghl_location_id: v })} testId="ghl-location-id-input" />
          <Field label="Private Integration Token" value={cfg.ghl_pit_token} onChange={(v) => update({ ghl_pit_token: v })} testId="ghl-pit-token-input" />
          <Field label="API base URL" value={cfg.ghl_api_base} onChange={(v) => update({ ghl_api_base: v })} />
          <Field label="API version" value={cfg.ghl_api_version} onChange={(v) => update({ ghl_api_version: v })} />
          <Field label="Pipeline ID (for reservations)" value={cfg.ghl_pipeline_id} onChange={(v) => update({ ghl_pipeline_id: v })} />
          <Field label="Stage ID" value={cfg.ghl_stage_id} onChange={(v) => update({ ghl_stage_id: v })} />
        </div>
        <div className="flex items-center gap-2 pt-2 flex-wrap">
          <Button variant="outline" className="border-[#EAEAE5]" onClick={async () => {
            await api.put(`/parks/${currentPark.id}`, cfg);
            try {
              await api.post("/integrations/ghl/test");
              toast.success("FreedomCRM connection verified");
            } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
          }} data-testid="ghl-test-button">Test connection</Button>
          <Button variant="outline" className="border-[#EAEAE5]" onClick={async () => {
            await api.put(`/parks/${currentPark.id}`, cfg);
            try {
              const { data } = await api.post("/integrations/ghl/tenants/sync-all");
              toast.success(`Pushed ${data.pushed} tenants${data.errors ? ` (${data.errors} errors)` : ""}`);
            } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
          }} data-testid="ghl-sync-all-tenants-button">Push all tenants</Button>
          <span className="text-[10px] text-[#737A76]">Inbound webhook URL: <code className="text-[#1A211D]">{`${window.location.origin}/api/integrations/ghl/webhook/${currentPark.id}`}</code></span>
        </div>
      </CardContent></Card>

      <UtilityPricingCard cfg={cfg} update={update} />

      <Card className="border-[#EAEAE5] shadow-none"><CardContent className="p-6 space-y-3">
        <div className="text-[10px] uppercase tracking-widest text-[#737A76]">Reservation Remarks</div>
        <Textarea value={cfg.reservation_remarks} onChange={(e) => update({ reservation_remarks: e.target.value })} className="min-h-[100px] bg-white border-[#EAEAE5]" />
      </CardContent></Card>
    </div>
  );
}

const UTILITIES = [
  { key: "electric", label: "Electric" },
  { key: "water",    label: "Water" },
  { key: "sewer",    label: "Sewer" },
  { key: "cable",    label: "Cable" },
  { key: "wifi",     label: "WiFi" },
  { key: "bbq",      label: "BBQ" },
];
const DEFAULT_PRICING = Object.fromEntries(UTILITIES.map((u) => [u.key, { inclusive: false, price: 0 }]));

function UtilityPricingCard({ cfg, update }) {
  const pricing = { ...DEFAULT_PRICING, ...(cfg.utility_pricing || {}) };
  const allInclusive = UTILITIES.every((u) => pricing[u.key]?.inclusive);

  const setUtil = (key, patch) => {
    update({
      utility_pricing: {
        ...pricing,
        [key]: { ...pricing[key], ...patch },
      },
    });
  };

  const setAllInclusive = (val) => {
    const next = {};
    for (const u of UTILITIES) next[u.key] = { ...pricing[u.key], inclusive: val };
    update({ utility_pricing: next });
  };

  return (
    <Card className="border-[#EAEAE5] shadow-none mb-4" data-testid="utility-pricing-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#737A76]">Utilities &amp; Pricing</div>
            <p className="text-xs text-[#737A76] mt-1">
              For each utility, choose <b>Inclusive</b> (bundled into rent — no extra charge) or set a <b>monthly price</b>.
              Stays under 30 days are always treated as inclusive on the invoice.
            </p>
          </div>
          <div className="flex items-center gap-2 bg-[#F0F0EC] px-3 py-2 rounded-md">
            <Switch
              checked={allInclusive}
              onCheckedChange={setAllInclusive}
              data-testid="all-inclusive-master-toggle"
            />
            <span className="text-sm font-medium text-[#1A211D]">All-Inclusive</span>
            <span className="text-[10px] text-[#737A76]">
              {allInclusive ? "every utility bundled into rent" : "configure each below"}
            </span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="utility-pricing-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[#737A76] text-left border-b border-[#EAEAE5]">
                <th className="py-2 pr-3">Utility</th>
                <th className="py-2 pr-3">Inclusive</th>
                <th className="py-2 pr-3">Monthly Price</th>
                <th className="py-2 text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {UTILITIES.map((u) => {
                const row = pricing[u.key];
                return (
                  <tr key={u.key} className="border-b border-[#EAEAE5]/60" data-testid={`utility-row-${u.key}`}>
                    <td className="py-2.5 pr-3 font-medium text-[#1A211D]">{u.label}</td>
                    <td className="py-2.5 pr-3">
                      <Switch
                        checked={!!row.inclusive}
                        onCheckedChange={(v) => setUtil(u.key, { inclusive: v })}
                        data-testid={`utility-inclusive-${u.key}`}
                      />
                    </td>
                    <td className="py-2.5 pr-3">
                      <div className="relative w-32">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[#737A76] text-sm pointer-events-none">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          disabled={!!row.inclusive}
                          value={row.inclusive ? "" : (row.price ?? "")}
                          onChange={(e) => setUtil(u.key, { price: parseFloat(e.target.value) || 0 })}
                          placeholder={row.inclusive ? "—" : "0.00"}
                          className="pl-5 bg-white border-[#EAEAE5] disabled:bg-[#F5F4EF] disabled:text-[#A8B0AB]"
                          data-testid={`utility-price-${u.key}`}
                        />
                      </div>
                    </td>
                    <td className="py-2.5 text-right">
                      {row.inclusive ? (
                        <span className="text-[11px] uppercase tracking-wider bg-[#E3F0E6] text-[#1F6336] px-2 py-1 rounded">
                          Included in rent
                        </span>
                      ) : row.price > 0 ? (
                        <span className="text-[11px] uppercase tracking-wider bg-[#FFF4D4] text-[#946A00] px-2 py-1 rounded">
                          Billed monthly
                        </span>
                      ) : (
                        <span className="text-[11px] uppercase tracking-wider bg-[#F0F0EC] text-[#737A76] px-2 py-1 rounded">
                          Not billed
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, value, onChange, type = "text", step, testId }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#737A76]">{label}</Label>
      <Input type={type} step={step} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="bg-white border-[#EAEAE5]" />
    </div>
  );
}
